import { useMemo, useState } from "react";
import { AuditDashboardStats, AuditTask } from "../../types";
import { JevisSelectOption } from "../ui/AppSelect";
import ProfileAvatar from "../ui/ProfileAvatar";
import AuditAssigneeMultiSelect from "./AuditAssigneeMultiSelect";
import AuditDatePicker from "../ui/AuditDatePicker";
import { PanelHero } from "../ui/panel";
import TaskProgressBar from "./TaskProgressBar";
import AuditCharts from "./AuditCharts";
import AssociateTaskPerformanceSection from "./AssociateTaskPerformance";
import { mergeDashboardStats } from "./auditChartUtils";

interface DbUser {
  id: number;
  username: string;
  role: string;
  is_active?: boolean;
  profile_picture_url?: string | null;
}

interface AdminAuditPanelProps {
  tasks: AuditTask[];
  users: DbUser[];
  stats: AuditDashboardStats | null;
  loading: boolean;
  statsLoading: boolean;
  submitting: boolean;
  error: string;
  onCreateTask: (payload: {
    assignee_user_ids: number[];
    target_count: number;
    due_date: string;
    notes?: string;
  }) => Promise<void>;
  onCancelTask: (taskId: number) => Promise<void>;
}

function formatDate(value: string) {
  try {
    return new Date(value).toLocaleDateString("it-IT", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return value;
  }
}

function defaultDueDate() {
  const d = new Date();
  d.setDate(d.getDate() + 7);
  return d.toISOString().slice(0, 10);
}

export default function AdminAuditPanel({
  tasks,
  users,
  stats,
  loading,
  statsLoading,
  submitting,
  error,
  onCreateTask,
  onCancelTask,
}: AdminAuditPanelProps) {
  const [assigneeSelection, setAssigneeSelection] = useState<JevisSelectOption[]>([]);
  const [targetCount, setTargetCount] = useState(5);
  const [dueDate, setDueDate] = useState(defaultDueDate());
  const [notes, setNotes] = useState("");
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);

  const chartStats = useMemo(() => mergeDashboardStats(stats, tasks), [stats, tasks]);
  const activeUsers = users.filter((u) => u.is_active !== false);
  const userOptions = activeUsers.map((u) => ({
    value: String(u.id),
    label: `${u.username}${u.role === "admin" ? " · admin" : ""}`,
  }));
  const activeTasks = tasks.filter((t) => t.status === "active");

  const tasksByUser = activeUsers.map((user) => ({
    user,
    tasks: activeTasks.filter((t) => t.assignee_user_id === user.id),
  }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (assigneeSelection.length === 0) return;
    await onCreateTask({
      assignee_user_ids: assigneeSelection.map((o) => Number(o.value)),
      target_count: targetCount,
      due_date: dueDate,
      notes: notes.trim() || undefined,
    });
    setNotes("");
    setAssigneeSelection([]);
  };

  const assigneeCount = assigneeSelection.length;

  return (
    <div className="space-y-8 text-left">
      <PanelHero
        eyebrow="Audit · Controllo settimanale"
        title="Pannello task team"
        description="Assegna obiettivi, monitora il progresso in tempo reale e verifica chi è in ritardo. Ricarica la pagina per aggiornare i dati."
      />

      <AuditCharts stats={chartStats} loading={statsLoading} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Form */}
        <form
          onSubmit={handleSubmit}
          className="rounded-2xl border border-secondary/10 bg-white p-6 shadow-lg shadow-secondary/5"
        >
          <div className="mb-5 flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-[#cd212a] to-[#a01820] text-lg text-white shadow-md">
              +
            </span>
            <div>
              <h3 className="font-extrabold text-secondary">Nuova task</h3>
              <p className="text-xs text-secondary/50">
                Stesso obiettivo per più utenti: ognuno avrà la propria task e progresso
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-secondary/50">
                Assegna a
              </label>
              <div className="mt-1.5">
                <AuditAssigneeMultiSelect
                  options={userOptions}
                  value={assigneeSelection}
                  onChange={setAssigneeSelection}
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-secondary/50">
                Lead da contattare
              </label>
              <div className="mt-1.5 flex items-center gap-3">
                <input
                  type="range"
                  min={1}
                  max={30}
                  value={targetCount}
                  onChange={(e) => setTargetCount(Number(e.target.value))}
                  className="h-2 flex-1 cursor-pointer accent-[#cd212a]"
                />
                <span className="flex h-10 w-14 items-center justify-center rounded-xl bg-secondary font-extrabold text-white text-lg">
                  {targetCount}
                </span>
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-secondary/50">
                Scadenza
              </label>
              <AuditDatePicker value={dueDate} onChange={setDueDate} />
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-secondary/50">
                Note
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Es. priorità startup toscane"
                className="mt-1.5 w-full resize-none rounded-xl border border-secondary/15 bg-secondary/[0.02] px-3 py-2.5 text-sm outline-none focus:border-primary/40 focus:ring-2 focus:ring-primary/15"
              />
            </div>

            {error && (
              <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting || assigneeCount === 0}
              className="w-full rounded-xl bg-gradient-to-r from-[#cd212a] to-[#a01820]  cursor-pointer py-3 text-sm font-extrabold text-white shadow-lg shadow-primary/25 transition hover:shadow-xl disabled:opacity-50"
            >
              {submitting
                ? "Assegnazione in corso..."
                : assigneeCount > 1
                  ? `Assegna ${assigneeCount} task →`
                  : "Assegna task →"}
            </button>
          </div>
        </form>

        {/* Team grid */}
        <div className="space-y-4">
          <div>
            <h3 className="text-sm font-extrabold uppercase tracking-wider text-secondary/50">
              Team · avanzamento live
            </h3>
            <p className="mt-0.5 text-xs text-secondary/40">
              Clicca su un utente per aprire le task attive
            </p>
          </div>

          {loading ? (
            <div className="space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-14 animate-pulse rounded-xl bg-secondary/10" />
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {tasksByUser.map(({ user, tasks: userTasks }) => {
                const totalTarget = userTasks.reduce((s, t) => s + t.target_count, 0);
                const totalDone = userTasks.reduce(
                  (s, t) => s + Math.min(t.completed_count, t.target_count),
                  0
                );
                const pct = totalTarget > 0 ? Math.round((totalDone / totalTarget) * 100) : 0;
                const isOpen = expandedUserId === user.id;
                const hasTasks = userTasks.length > 0;

                return (
                  <div
                    key={user.id}
                    className={`overflow-hidden rounded-xl border bg-white shadow-sm transition ${
                      isOpen ? "border-primary/25 ring-1 ring-primary/10" : "border-secondary/10"
                    }`}
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedUserId((prev) => (prev === user.id ? null : user.id))
                      }
                      className="flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left transition hover:bg-secondary/[0.03]"
                      aria-expanded={isOpen}
                    >
                      <ProfileAvatar
                        name={user.username}
                        src={user.profile_picture_url}
                        size="sm"
                        className="rounded-full"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-extrabold text-secondary">{user.username}</p>
                        <p className="text-xs text-secondary/50">
                          {hasTasks ? (
                            <>
                              {userTasks.length} task · {totalDone}/{totalTarget} lead
                              <span className="ml-1 font-semibold text-primary">{pct}%</span>
                            </>
                          ) : (
                            "Nessuna task attiva"
                          )}
                        </p>
                      </div>
                      <span
                        className={`shrink-0 cursor-pointer text-secondary/40 transition-transform ${isOpen ? "rotate-180" : ""}`}
                        aria-hidden
                      >
                        ▼
                      </span>
                    </button>

                    {isOpen && hasTasks && (
                      <div className="space-y-2 border-t border-secondary/10 bg-secondary/[0.02] px-4 py-3">
                        {userTasks.map((task) => (
                          <div
                            key={task.id}
                            className={`rounded-xl p-3 ${
                              task.overdue
                                ? "bg-red-50/80 ring-1 ring-red-100"
                                : "bg-white ring-1 ring-secondary/5"
                            }`}
                          >
                            <div className="mb-2 flex items-start justify-between gap-2">
                              <p className="text-xs font-bold text-secondary">
                                {task.target_count} lead · scadenza {formatDate(task.due_date)}
                                {task.overdue && (
                                  <span className="ml-1 text-red-600">· Scaduta</span>
                                )}
                              </p>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onCancelTask(task.id);
                                }}
                                className="shrink-0 cursor-pointer text-[10px] font-bold uppercase tracking-wider text-secondary/40 hover:text-red-600"
                              >
                                Annulla
                              </button>
                            </div>
                            <TaskProgressBar
                              completed={task.completed_count}
                              target={task.target_count}
                              overdue={task.overdue}
                              size="sm"
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    {isOpen && !hasTasks && (
                      <p className="border-t border-secondary/10 px-4 py-4 text-center text-xs text-secondary/40">
                        Nessuna task attiva per questo utente
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <AssociateTaskPerformanceSection tasks={tasks} users={activeUsers} />
    </div>
  );
}
